import type { Rgba } from '../color.ts';
import type { Environment } from './environment.ts';
import type { MapLook } from './look.ts';
import type { Selection } from './selection.ts';
import type { MapClipboard } from './transform.ts';
import type { MapDocumentVersion } from './version.ts';

export type Vec2 = {
  x: number;
  y: number;
};

export type MapBounds = {
  width: number;
  height: number;
};

/** A control point that bends a straight edge into a quadratic curve, or null for a straight edge. */
export type RingEdge = {
  bend: Vec2 | null;
};

/**
 * The shape shared by islands, shallow-water zones, and the start area: a closed polygon whose
 * corners are automatically filleted (`cornerRadius`) and whose edges can each be individually
 * bent (`edges[i].bend`). `edges[i]` connects `vertices[i]` to `vertices[(i + 1) % n]`.
 */
export type Ring = {
  vertices: Vec2[];
  edges: RingEdge[];
  cornerRadius: number;
};

/** Bitmap prop planted on an island plateau. Maps store the id; textures live in the catalog. */
export type PropId = 'palm-a' | 'palm-b' | 'palm-c' | 'lighthouse';

export type IslandDecoration = {
  id: string;
  catalog: PropId;
  x: number;
  y: number;
  scale: number;
  rotation: number;
};

/**
 * Metres above the immediate support (innermost containing land, or water).
 * Columns and shade use the stacked world height. `skirtM` / `decorations` are
 * look only — collision stays on the waterline.
 */
export type Island = {
  id: string;
  color: Rgba;
  elevation: number;
  skirtM: number;
  decorations: IslandDecoration[];
  nextDecorationId: number;
} & Ring;

/**
 * A rock outcrop. Structurally an island made of stone. Paint order follows elevation (islands
 * under rocks when the heights match) on one Graphics list so the two kinds can interleave.
 */
export type Rock = { id: string; color: Rgba; elevation: number } & Ring;

/** Gameplay semantics of a shallow zone. Presentation lives in `color`, independently. */
export type WaterMaterial = 'sand' | 'stone' | 'reef';

/**
 * A bed under the water sheet. `depth` is metres below the surface (look today; keel later).
 * `material` is sand / stone / reef — a reef is this material, not a second body type.
 */
export type ShallowWaterZone = {
  id: string;
  material: WaterMaterial;
  color: Rgba;
  depth: number;
} & Ring;

/** At most one per map. */
export type StartArea = { id: string; color: Rgba } & Ring;

/** Optional post-finish wait ring. Finished boats autopilot here when present. */
export type ParkArea = { id: string; color: Rgba } & Ring;

export type Checkpoint = {
  id: string;
  a: Vec2;
  b: Vec2;
  /**
   * null = any order, each must still be crossed. A number > 0 is one step of the sequence;
   * gates that share a number are alternatives (sailing through one clears the rest).
   */
  order: number | null;
  isGoal: boolean;
};

/**
 * Open measuring polyline. Editor-only; play ignores it. Authored `points` are the click vertices;
 * `cornerRadius` fillets interiors the same way a ring does (`tessellateOpenPath`).
 */
export type MapRoute = {
  id: string;
  color: Rgba;
  points: Vec2[];
  cornerRadius: number;
};

/** Authored credits. `revision` is the map release (UI: Version), not schema `version`. */
export type MapMeta = {
  author: string;
  revision: string;
  description: string;
  notes: string;
};

export type MapDocument = {
  version: MapDocumentVersion;
  /** Player-facing title. Empty means the catalog falls back to the filename label. */
  name: string;
  meta: MapMeta;
  bounds: MapBounds;
  environment: Environment;
  look: MapLook;
  islands: Island[];
  rocks: Rock[];
  shallowWater: ShallowWaterZone[];
  checkpoints: Checkpoint[];
  /**
   * Times the checkered line must be crossed to finish. 1 is a single run.
   * Each earlier crossing starts the next lap and reopens every gate.
   */
  laps: number;
  routes: MapRoute[];
  startArea: StartArea | null;
  /** Optional. Early finishers autopilot here while others still race. */
  parkArea: ParkArea | null;
  nextIslandId: number;
  nextRockId: number;
  nextShallowWaterId: number;
  nextCheckpointId: number;
  nextRouteId: number;
};

export type EditorTool =
  | 'select'
  | 'hand'
  | 'scale'
  | 'rotate'
  | 'draw-island'
  | 'draw-rock'
  | 'draw-terrace'
  | 'draw-water'
  | 'place-checkpoint'
  | 'edit-start-area'
  | 'edit-park-area'
  | 'draw-route'
  | 'place-prop'
  | 'erase-prop';

/** Draft kind for Draw terraces — each close emits only islands or only rocks. */
export type TerraceKind = 'island' | 'rock';

export type CheckpointFlag = 'a' | 'b';

export type EditorSession = {
  tool: EditorTool;
  draft: Vec2[];
  draftMaterial: WaterMaterial;
  /** Session-only; written onto the zone at close. */
  draftDepth: number;
  draftProp: PropId;
  draftTerraceKind: TerraceKind;
  draftTerraceLevels: number;
  draftTerracePeakM: number;
  draftTerraceRadius: number;
  draftTerraceColor: Rgba;
  draftTerraceColorVariation: number;
  draftTerraceJitter: number;
  draftTerraceElevRand: number;
  draftTerraceRadiusRand: number;
  checkpointDraft: Vec2 | null;
  selection: Selection;
  /** Survives tool switches and undo, like a system clipboard. */
  clipboard: MapClipboard | null;
  /** An authoring aid only; nothing about it is written to the map. */
  snapToGrid: boolean;
};

/** 16384 px is 1638 m at 10 px/m, roughly an 80-turn crossing at the movement cap. */
export const DEFAULT_MAP_WIDTH = 16384;
export const DEFAULT_MAP_HEIGHT = 16384;
export const MIN_MAP_SIZE = 2048;
export const MAX_MAP_SIZE = 32768;

export const MIN_RING_VERTICES = 3;
export const MIN_ROUTE_VERTICES = 2;
export const DEFAULT_CORNER_RADIUS = 200;
/** Island Radius scrub max. Routes always fillet at this so each bend is as round as the UI allows. */
export const MAX_CORNER_RADIUS = 500;
export const DEFAULT_GATE_WIDTH = 120;

/** Authored lap count. Missing or invalid files migrate to 1. */
export const MIN_LAPS = 1;
export const MAX_LAPS = 99;

/** How far a body sticks up from the water or the land it sits on, in metres. */
export const MIN_ELEVATION = 0;
/**
 * Authoring ceiling, not a physical one. Extrusion is linear (`visualRisePx`) and water
 * cast caps at `CAST_OFFSET_CAP_PX`, so this only stops a typo from leaning a plateau
 * thousands of pixels north. 500 m is a tall coastal cliff next to a 7 m hull.
 */
export const MAX_ELEVATION = 500;
export const DEFAULT_ISLAND_ELEVATION = 4;
export const DEFAULT_ROCK_ELEVATION = 10;

/** Depth below the surface, in metres, for a shallow-water bed. */
export const MIN_WATER_DEPTH = 0;
export const MAX_WATER_DEPTH = 20;
export const DEFAULT_WATER_DEPTH = 3;
